import React, { useState } from 'react';
import Button from '@components-clean/core/button';
import DataListingProgressCard from '@components-clean/core/data-listing-progress-card';
import ReviewProgressCard from '@components-clean/core/review-progress-card';

const TOTAL_SUBJECTS = 10;

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

const toggleInSet = (prev: Set<number>, id: number): Set<number> => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
};

const ProgressBarsPage = () => {
    const [reviewedSubjects, setReviewedSubjects] = useState<Set<number>>(new Set());

    const [listing1ReviewedSubjects, setListing1ReviewedSubjects] = useState<Set<number>>(new Set());
    const [listing2ReviewedSubjects, setListing2ReviewedSubjects] = useState<Set<number>>(new Set());
    const [listing3ReviewedSubjects, setListing3ReviewedSubjects] = useState<Set<number>>(new Set());

    const markAllSubjects = () => setReviewedSubjects(new Set(range(TOTAL_SUBJECTS)));
    const clearAllSubjects = () => setReviewedSubjects(new Set());

    return (
        <div>
            <h2>Progress Bars</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
                <section>
                    <h3>Review Progress Card</h3>
                    <p style={{ color: '#666', marginTop: 0 }}>
                        Subject-level review. Tick subjects to see the bar advance — In Progress is derived as Total −
                        Reviewed.
                    </p>

                    <div style={{ maxWidth: 420 }}>
                        <ReviewProgressCard
                            title="Subject Review Progress"
                            reviewedCount={reviewedSubjects.size}
                            totalCount={TOTAL_SUBJECTS}
                        />
                    </div>

                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                        <Button data-testid="mark-all-subjects" primary onClick={markAllSubjects}>
                            Mark all reviewed
                        </Button>
                        <Button data-testid="clear-all-subjects" onClick={clearAllSubjects}>
                            Clear
                        </Button>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                        {range(TOTAL_SUBJECTS).map((i) => {
                            const id = `subject-${i}`;
                            return (
                                <li key={i} style={{ padding: '4px 0' }}>
                                    <input
                                        id={id}
                                        type="checkbox"
                                        checked={reviewedSubjects.has(i)}
                                        onChange={() => setReviewedSubjects((prev) => toggleInSet(prev, i))}
                                    />{' '}
                                    <label htmlFor={id} style={{ cursor: 'pointer' }}>
                                        Subject {i + 1}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                <section>
                    <h3>Data Listing Progress Card</h3>
                    <p style={{ color: '#666', marginTop: 0 }}>
                        Per-listing review. Tick individual records or use the &quot;Mark whole listing&quot; button.
                    </p>

                    <div style={{ maxWidth: 420 }}>
                        <DataListingProgressCard
                            title="Adverse Events"
                            totalCount={TOTAL_SUBJECTS}
                            rows={[
                                {
                                    id: 'listing-1',
                                    name: 'Listing 1',
                                    reviewedCount: listing1ReviewedSubjects.size,
                                },
                                {
                                    id: 'listing-2',
                                    name: 'Listing 2',
                                    reviewedCount: listing2ReviewedSubjects.size,
                                },
                                {
                                    id: 'listing-3',
                                    name: 'Listing 3',
                                    reviewedCount: listing3ReviewedSubjects.size,
                                },
                            ]}
                        />
                    </div>

                    <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                        <div>
                            <h4 style={{ margin: '0 0 8px' }}>Listing 1</h4>
                            <Button
                                data-testid="listing-1-mark-all-subjects"
                                primary
                                onClick={() => setListing1ReviewedSubjects(new Set(range(TOTAL_SUBJECTS)))}
                            >
                                Mark all reviewed
                            </Button>
                            <Button
                                data-testid="listing-1-clear-all-subjects"
                                onClick={() => setListing1ReviewedSubjects(new Set())}
                            >
                                Clear
                            </Button>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {range(TOTAL_SUBJECTS).map((i) => {
                                    const id = `listing-1-subject-${i}`;
                                    return (
                                        <li key={i} style={{ padding: '4px 0' }}>
                                            <input
                                                id={id}
                                                type="checkbox"
                                                checked={listing1ReviewedSubjects.has(i)}
                                                onChange={() =>
                                                    setListing1ReviewedSubjects((prev) => toggleInSet(prev, i))
                                                }
                                            />{' '}
                                            <label htmlFor={id} style={{ cursor: 'pointer' }}>
                                                Subject {i + 1}
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 8px' }}>Listing 2</h4>
                            <Button
                                data-testid="listing-2-mark-all-subjects"
                                primary
                                onClick={() => setListing2ReviewedSubjects(new Set(range(TOTAL_SUBJECTS)))}
                            >
                                Mark all reviewed
                            </Button>
                            <Button
                                data-testid="listing-2-clear-all-subjects"
                                onClick={() => setListing2ReviewedSubjects(new Set())}
                            >
                                Clear
                            </Button>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {range(TOTAL_SUBJECTS).map((i) => {
                                    const id = `listing-2-subject-${i}`;
                                    return (
                                        <li key={i} style={{ padding: '4px 0' }}>
                                            <input
                                                id={id}
                                                type="checkbox"
                                                checked={listing2ReviewedSubjects.has(i)}
                                                onChange={() =>
                                                    setListing2ReviewedSubjects((prev) => toggleInSet(prev, i))
                                                }
                                            />{' '}
                                            <label htmlFor={id} style={{ cursor: 'pointer' }}>
                                                Subject {i + 1}
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 8px' }}>Listing 3</h4>
                            <Button
                                data-testid="listing-3-mark-all-subjects"
                                primary
                                onClick={() => setListing3ReviewedSubjects(new Set(range(TOTAL_SUBJECTS)))}
                            >
                                Mark all reviewed
                            </Button>
                            <Button
                                data-testid="listing-3-clear-all-subjects"
                                onClick={() => setListing3ReviewedSubjects(new Set())}
                            >
                                Clear
                            </Button>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {range(TOTAL_SUBJECTS).map((i) => {
                                    const id = `listing-3-subject-${i}`;
                                    return (
                                        <li key={i} style={{ padding: '4px 0' }}>
                                            <input
                                                id={id}
                                                type="checkbox"
                                                checked={listing3ReviewedSubjects.has(i)}
                                                onChange={() =>
                                                    setListing3ReviewedSubjects((prev) => toggleInSet(prev, i))
                                                }
                                            />{' '}
                                            <label htmlFor={id} style={{ cursor: 'pointer' }}>
                                                Subject {i + 1}
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ProgressBarsPage;
